import {useAppDispatch, useAppSelector, useLanguage, useLoader, useModal, useUserAgent} from "../../../hooks/hooks";
import {
    getProductImagesAsync,
    getTheLatestProductDetailAsync,
    productDetail,
    productStatus,
    setProduct
} from "../../../store/product/productSlice";
import {orderStatus} from "../../../store/order/orderSlice";
import {
    DisplayPosition,
    ProductDetailImageDTO,
    ProductFile,
    ProductFileType,
    ProductImage
} from "../../../types/product";
import React, {Fragment, useCallback, useEffect, useMemo, useState} from "react";
import {MainWrapper} from "../MainWrapper";
import style from "../../../assets/scss/sub/purchase/purchaseVoucher.module.scss";
import {ArticleTitle} from "../ArticleTitle";
import minusBtnIcon from "../../../assets/images/sub/purchase_voucher/quantity_minus_btn.svg";
import plusBtnIcon from "../../../assets/images/sub/purchase_voucher/quantity_plus_btn.svg";
import voucherIcon from "../../../assets/images/sub/purchase_voucher/voucher_icon.svg";
import {PurchaseReviewField} from "./PurchaseReviewField";
import {usePurchaseVoucherFunctions} from "./hooks/usePurchaseVoucherFunctions";
import {useTranslation} from "react-i18next";

interface Price {
    regularPrice: number;
    sellingPrice: number;
}

export function ProductVisibleVoucher({type}: { type: string }) {
    const { t } = useTranslation("productDetail");
    // 임시 처리
    const { openAlertModal } = useModal();

    const statusProduct = useAppSelector(productStatus);
    const statusOrder = useAppSelector(orderStatus);
    const productDetailInfo = useAppSelector(productDetail);
    const dispatch = useAppDispatch();
    const { language } = useLanguage();

    const { setLoaderStatus } = useLoader();
    const { isMobile } = useUserAgent();

    const purchaseType = type === 'voucher' ? DisplayPosition.USER_WEB_KPASS : DisplayPosition.COUNSELOR_VOUCHER;
    const titleWord = type === "voucher" ? t('voucher.title') : t('counseling.title')

    const [quantity, setQuantity] = useState(1);
    const [productImages, setProductImages] = useState<ProductFile[]>([]);
    const [productImagesMobile, setProductImagesMobile] = useState<ProductFile[]>([]);

    const {
        getDiscountText,
        getDetailImageAndVideoComponents,
        onClickPurchaseBtn
    } = usePurchaseVoucherFunctions();

    const getSortedImageUrls = useCallback(
        (images: ProductImage[]) =>
            images
                .sort((a, b) => a.orderNum - b.orderNum)
                .map((img) => {
                    const url = img.file.uri;
                    return {
                        type: ProductFileType.getFileTypeFromUrl(url),
                        url,
                    };
                }),
        []
    );

    const getProductDetail = useCallback(async () => {
        try {
            const result = await dispatch(getTheLatestProductDetailAsync({
                condition: {language},
                displayPosition: purchaseType
            })).unwrap();
            const {desktopImages, mobileImages}: ProductDetailImageDTO = await dispatch(getProductImagesAsync({
                    condition: {productIdx: result.idx, language},
                    displayPosition: purchaseType
                })
            ).unwrap();

            setProductImages(getSortedImageUrls(desktopImages));
            setProductImagesMobile(getSortedImageUrls(mobileImages));

            dispatch(setProduct(result));
        } catch (error) {
            console.error(error);
            openAlertModal(null);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [openAlertModal, language, type]);

    const onClickAddQtyBtn = () => {
        setQuantity(quantity + 1);
    };

    const onClickSubtractQtyBtn = () => {
        if (quantity > 1) {
            setQuantity(quantity - 1);
        }
    };

    const totalPrice: Price = useMemo(() => {
        if (productDetailInfo) {
            return {
                regularPrice: productDetailInfo.regularPrice * quantity,
                sellingPrice: productDetailInfo.sellingPrice * quantity,
            };
        } else {
            return {
                regularPrice: 0,
                sellingPrice: 0,
            };
        }
    }, [quantity, productDetailInfo]);

    useEffect(() => {
        document.title = `${titleWord} | ${process.env.REACT_APP_TITLE}`;
        window.scroll({ top: 0 });
        getProductDetail().then();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [language]);

    useEffect(() => {
        setLoaderStatus([statusOrder]);
    }, [setLoaderStatus, statusOrder]);

    useEffect(() => {
        setLoaderStatus([statusProduct]);
    }, [setLoaderStatus, statusProduct]);

    useEffect(() => {
        getProductDetail().then();
        // eslint-disable-next-line
    }, [type]);

    const hasDiscountValue = useMemo(
        () => Boolean(productDetailInfo?.discountValue && productDetailInfo.discountValue > 0),
        [productDetailInfo]
    );

    return (
        <MainWrapper showFooter={true} mainClassName={style.main}>
            <>
                <div className={style.title}>
                    <div className={style.bgItem1}></div>
                    <ArticleTitle title={titleWord} className={style.textArea} />
                </div>
                <div className={style.productDetail}>
                    <div className={style.thumbnail}>
                        {productDetailInfo?.thumbnail.uri && (
                            <img src={productDetailInfo?.thumbnail.uri} alt={"상품 썸네일 이미지"} />
                        )}
                    </div>
                    <div className={style.desc}>
                        <span className={style.title}>{productDetailInfo?.name}</span>
                        <ul className={style.descList}>
                            {productDetailInfo?.description?.map((desc, idx) => (
                                <li key={`normal-desc-${idx}`}>{desc}</li>
                            ))}
                            {productDetailInfo?.specialDescription?.map((desc, idx) => (
                                <li key={`special-desc-${idx}`} className={style.accent}>
                                    {desc}
                                </li>
                            ))}
                        </ul>
                        <div className={style.dashLine}></div>
                        <div className={style.quantityAndPrice}>
                            <div className={style.quantity}>
                                <button className={style.qtyBtn}>
                                    <img src={minusBtnIcon} alt={"수량 차감 아이콘"} onClick={onClickSubtractQtyBtn} />
                                </button>
                                <span className={style.number}>{quantity}</span>
                                <button className={style.qtyBtn}>
                                    <img src={plusBtnIcon} alt={"수량 추가 아이콘"} onClick={onClickAddQtyBtn} />
                                </button>
                            </div>
                            {hasDiscountValue && (
                                <div className={style.regularPrice}>{totalPrice.regularPrice.toLocaleString()} {t("unit")}</div>
                            )}
                            <div className={`${style.discountPrice} ${hasDiscountValue ? style.positionBottomRow : ""}`}>
                                {hasDiscountValue && (
                                    <>
                                        <img className={style.icon} src={voucherIcon} alt="상품 아이콘" />
                                        <span className={style.badge}>{getDiscountText(productDetailInfo, quantity)} {t("saleAmount")}</span>
                                    </>
                                )}
                                <span className={style.price}>{totalPrice.sellingPrice.toLocaleString()} {t("unit")}</span>
                            </div>
                        </div>
                        <button className={style.purchaseBtn} onClick={(e) => onClickPurchaseBtn(e,productDetailInfo, quantity, type)}>
                            {t("btn.purchase")}
                        </button>
                    </div>
                </div>
                <div className={style.imageArea}>
                    <div className={style.bgItem1}></div>
                    <div className={style.bgItem2}></div>
                    <div className={style.bgItem3}></div>
                    {isMobile ? (
                        <>{getDetailImageAndVideoComponents(productImagesMobile)}</>
                    ) : (
                        <>{getDetailImageAndVideoComponents(productImages)}</>
                    )}
                </div>
                <PurchaseReviewField />
            </>
        </MainWrapper>
    );
}